import { useSelector } from 'react-redux';

import Form from 'app/components/Form';
import useForm from 'app/hooks/useForm';

import {
  createOption,
  formatOptions,
  loginTypeOptions,
  posTypeOptions,
  checkoutTypeOptions,
  printModeOptions
} from 'app/utils/options';

import {
  CATALOG_SCREEN,
  GALLERY_SCREEN,
  DOUBLE_PANE_RECEIPT,
  SINGLE_PANE_RECEIPT,
  LEGACY_THEME,
  CLUBMED_THEME,
  HPX_THEME,
  ICECASTLE_THEME
} from 'shared/utils/Constants';

const KioskForm = props => {
  const {
    kiosk,
    onClose,
    onSubmit,
    action,
    jobConfigs,
    jobs
  } = props;

  const account = useSelector(state => state.portalReducer.account);

  // remove Digital Delivery from product options, n/a to kiosks
  const jobConfigOptions = formatOptions(jobConfigs.filter(jc => {
    return jc.job_config_template_id !== -1;
  }));

  const jobOptions = formatOptions(jobs);

  const startScreenOptions = [
    createOption('Catalog Screen', CATALOG_SCREEN),
    createOption('Gallery Screen', GALLERY_SCREEN)
  ]

  const receiptScreenTypes = [
    createOption('Double Pane (Club Med)', DOUBLE_PANE_RECEIPT),
    createOption('Single Pane', SINGLE_PANE_RECEIPT)
  ]

  let header = null;
  if (action === 'update') {
    header = 'Edit Fields';
  }

  // hard code the theme based on account flags.
  // i.e. all accounts will have legacy theme unless hard coded otherwise
  let theme = LEGACY_THEME;
  if (account.feature_flags.club_med) {
    theme = CLUBMED_THEME;
  }
  if (account.feature_flags.hpx) {
    theme = HPX_THEME;
  }
  if (account.feature_flags.ice_castle) {
    theme = ICECASTLE_THEME;
  }

  let fields = [
    {
      type: 'section', label: 'General', fields: [
        { name: 'name', label: 'Kiosk name', type: 'text', value: kiosk?.name, helperText: 'This will be the name of the kiosk' },
        { name: 'header', label: 'Header', type: 'text', value: kiosk?.header },
        { name: 'theme', type: 'hidden', value: theme },
        { name: 'job_id', label: 'Main Job', type: 'select', value: kiosk?.job_id, valueType: 'integer', options: jobOptions, hideNoneOption: true, helperText: 'Main job for the kiosk' },
      ]
    },
    {
      type: 'section', label: 'Login', fields: [
        { name: 'login_type', label: 'Login Type', type: 'select', value: kiosk?.login_type, options: loginTypeOptions, valueType: 'integer', hideNoneOption: true, helperText: 'Set kiosk theme' },
        { name: 'create_customer', label: 'Create customer if missing', type: 'checkbox', valueType: 'boolean', value: kiosk?.create_customer },
        { name: 'set_mandatory_email', label: 'Set Mandatory Email for Access Code Login', type: 'checkbox', valueType: 'boolean', value: kiosk?.set_mandatory_email },
      ]
    },
    {
      type: 'section', label: 'Pricing', fields: [
        { name: 'checkout_type', label: 'Checkout Type', type: 'select', value: kiosk?.checkout_type, options: checkoutTypeOptions, valueType: 'integer', hideNoneOption: true, helperText: 'Set kiosk checkout type' },
        { name: 'offsite_pos_type', label: 'POS Type', type: 'select', value: kiosk?.offsite_pos_type, options: posTypeOptions, valueType: 'integer', hideNoneOption: true, helperText: 'Set description for POS type' },
        { name: 'job_config_id', label: 'Product', type: 'select', value: kiosk?.job_config_id, valueType: 'integer', options: jobConfigOptions, hideNoneOption: true, helperText: 'Pricing configuration for the kiosk' },
        { name: 'tax_rate', label: 'Tax Rate', type: 'number', valueType: 'number', value: kiosk?.tax_rate, helperText: 'Set Tax for Haggle Mode & Kiosk Pricing' },
        { name: 'show_change', label: 'Show Change', type: 'checkbox', valueType: 'boolean', value: kiosk?.show_change },
        { name: 'set_mandatory_customer_name', label: 'Set Mandatory Customer Name upon Purchase', type: 'checkbox', valueType: 'boolean', value: kiosk?.set_mandatory_customer_name },
        { name: 'hide_buy_more', label: 'Hide Buy More', type: 'checkbox', valueType: 'boolean', value: kiosk?.hide_buy_more },
        { name: 'stripe_enabled', label: 'Stripe', type: 'checkbox', valueType: 'boolean', value: kiosk?.stripe_enabled },
        { name: 'tilby_enabled', label: 'Tilby', type: 'checkbox', valueType: 'boolean', value: kiosk?.tilby_enabled },
        { name: 'stripe_reader_enabled', label: 'Stripe Reader', type: 'checkbox', valueType: 'boolean', value: kiosk?.stripe_reader_enabled },
        { name: 'offsite_pos_enabled', label: 'Offsite POS', type: 'checkbox', valueType: 'boolean', value: kiosk?.offsite_pos_enabled },
        { name: 'receipt_screen_type', label: 'Receipt Screen Type', type: 'select', value: kiosk?.receipt_screen_type, options: receiptScreenTypes, valueType: 'integer', hideNoneOption: true, helperText: 'Receipt display on screen' },
      ]
    },
    {
      type: 'section', label: 'Screens', fields: [
        { name: 'catalog_screen', label: 'Catalog Screen', type: 'checkbox', valueType: 'boolean', value: kiosk?.catalog_screen },
        { name: 'gallery_screen', label: 'Gallery Screen', type: 'checkbox', valueType: 'boolean', value: kiosk?.gallery_screen },
        { name: 'cart_screen', label: 'Cart Screen', type: 'checkbox', valueType: 'boolean', value: kiosk?.cart_screen },
        { name: 'start_screen', label: 'Start Screen', type: 'select', value: kiosk?.start_screen, options: startScreenOptions, valueType: 'integer', hideNoneOption: true, helperText: 'Start on screen' },
      ]
    },
    {
      type: 'section', label: 'Miscellaneous', fields: [
        { name: 'print_mode', label: 'Print Mode', type: 'select', value: kiosk?.print_mode, options: printModeOptions, valueType: 'integer', hideNoneOption: true, helperText: 'Set kiosk print mode' },
        { name: 'show_dates', label: 'Show Dates', type: 'checkbox', valueType: 'boolean', value: kiosk?.show_dates },
        { name: 'hide_order_details', label: 'Hide Order Details', type: 'checkbox', valueType: 'boolean', value: kiosk?.hide_order_details },
        { name: 'uppercase_labels', label: 'Uppercase Labels', type: 'checkbox', valueType: 'boolean', value: kiosk?.uppercase_labels },
        { name: 'show_aspect', label: 'Show Aspect Ratio', type: 'checkbox', valueType: 'boolean', value: kiosk?.show_aspect },
        { name: 'enable_edits', label: 'Enable Editing', type: 'checkbox', valueType: 'boolean', value: kiosk?.enable_edits },
        { name: 'timeout', label: 'Auto Logout Timeout (seconds)', type: 'number', valueType: 'integer', value: kiosk?.timeout },
        { name: 'retention', label: 'Photo Retention (days)', type: 'number', valueType: 'integer', value: kiosk?.retention },
        { name: 'opt_in_checked', label: 'Promotions Opt In Checked?', type: 'checkbox', valueType: 'boolean', value: kiosk?.opt_in_checked },
        { name: 'enable_email_verification', label: 'Enable Email Verification upon purchase', type: 'checkbox', valueType: 'boolean', value: kiosk?.enable_email_verification },
      ]
    },
  ]

  return (
    <Form {...{
      header,
      fields,
      action,
      onClose,
      onSubmit
    }} />
  );
}

export default KioskForm;
